import { lazy, useEffect, Suspense } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { useAppDispatch, useAppSelector } from "./redux/store.ts";
import { getUserData } from "./features/User/redux/api.ts";
import { logger } from "./utils/consoleLogger.ts";

import { getCurrentUserDetailsFromLocalStorage } from "./api/authApi.ts";
import AppRoutes from "./routes/AppRoutes.tsx";
import OverlayProvider from "./context/OverlayContext.tsx";
import PostHogProvider from "./context/PostHogProvider.tsx";
import { GoogleOAuthProvider } from "@react-oauth/google";

import { setLoaderForUser } from "./features/User/redux/slice.ts";
import { isSlug } from "./lib/util.ts";

const ErrorPage = lazy(() => import("./pages/ErrorPage.tsx"));
const Loader = lazy(() => import("./shared/loader/LoaderPage.tsx"));

function App() {
  const { loading, requestedSlug } = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const userDetails = useAppSelector((state) => state?.user?.user);

  // useLocation (not window.location) so slug changes re-render and re-run effects
  const location = useLocation();
  const slug = location.pathname.split("/")[1];
  const navigate = useNavigate();
  const userAuthMetaData = getCurrentUserDetailsFromLocalStorage();
  const isActualSlug = isSlug();

  useEffect(() => {
    if (userAuthMetaData) {
      if (userAuthMetaData.needsOnboarding) {
        // Mid-onboarding there is no company to fetch — firing getUserData
        // here guarantees a 4xx whose handler clears localStorage, which
        // kicked freshly-verified users back to the login page. And any
        // other path (e.g. "/") renders the login catch-all, so steer
        // back to the form.
        dispatch(setLoaderForUser(false));
        if (!window.location.pathname.startsWith("/onboard-form")) {
          navigate("/onboard-form/", { replace: true });
        }
        return;
      }
      const slugCheck = isActualSlug ? slug : "";
      // Re-fetch whenever the URL points at a company we haven't loaded —
      // the backend authorizes any slug the user has an active membership for
      if (slugCheck !== requestedSlug) dispatch(getUserData(slugCheck));
    } else dispatch(setLoaderForUser(false));
  }, [slug]);

  useEffect(() => {
    if (!userAuthMetaData || !isActualSlug || loading) return;
    // Only judge access once the fetch for THIS URL's slug has completed;
    // comparing against a previous company's data would bounce every switch
    if (requestedSlug !== (slug || "")) return;

    if (!slug) {
      logger.error("No slug found in URL, redirecting", { path: window.location.pathname });
      navigate(`/${userDetails?.slug ?? userAuthMetaData?.slug}`);
    } else if (userDetails?.slug && userDetails.slug !== slug) {
      // Backend resolved a different company for this slug → no membership.
      // Land on that company's home; the requested deep path belongs to a
      // company this user can't see.
      navigate(`/${userDetails.slug}`, { replace: true });
    }
  }, [slug, userDetails, loading, requestedSlug]);

  if (loading)
    return (
      <main className="flex h-screen w-screen items-center justify-center">
        <Loader />
      </main>
    );

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <Suspense fallback={<Loader />}>
        <ErrorBoundary FallbackComponent={ErrorPage}>
          <PostHogProvider>
            <OverlayProvider>
              <AppRoutes />
            </OverlayProvider>
          </PostHogProvider>
        </ErrorBoundary>
      </Suspense>
    </GoogleOAuthProvider>
  );
}

export default App;
